import { useEffect, useRef, useState } from 'react';
import { Box, FormHelperText } from '@mui/material';
import PropTypes from 'prop-types';

import CodeEditor from '../../scraper/CodeEditor';
import { sameDocument } from '../../../utils/jsonObjectValue';

const toText = (value) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value;
    return JSON.stringify(value, null, 2);
};

/**
 * One JSON object inside the settings — an action's `input`, say — in Monaco.
 *
 * The contract is DialogJsonField's, one level down: text that parses to an object
 * is emitted as that object, blank as undefined (the key is removed), and anything
 * else as the text itself, which settingsProblems then refuses, so Save is held
 * rather than the form quietly keeping an older value than the one on screen.
 */
export default function JsonObjectEditor({ value, onChange, path, disabled, error, helperText, height = 140 }) {
    const [text, setText] = useState(() => toText(value));
    const emitted = useRef(value);

    // A value that did not come from here — a reset, Raw, another record.
    useEffect(() => {
        if (!sameDocument(value, emitted.current)) {
            emitted.current = value;
            setText(toText(value));
        }
    }, [value]);

    const handleChange = (next) => {
        setText(next);
        let out;
        if (next.trim() === '') {
            out = undefined;
        } else {
            try {
                const parsed = JSON.parse(next);
                out = parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : next;
            } catch {
                out = next;
            }
        }
        emitted.current = out;
        onChange(out);
    };

    return (
        <Box>
            <CodeEditor
                language="json"
                path={path}
                value={text}
                onChange={handleChange}
                readOnly={disabled}
                sx={{ height, borderColor: error ? 'error.main' : 'divider' }}
            />
            {helperText && <FormHelperText error={Boolean(error)}>{helperText}</FormHelperText>}
        </Box>
    );
}

JsonObjectEditor.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    path: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.node,
    height: PropTypes.number
};
